import { remove, send } from './client';
import { query, type Cursor, type CursorPage, type OffsetPage } from './paging';

/**
 * One gateway's update state, computed only by the server: no target, waiting to be sent, waiting for the
 * maintenance window, offline, sent, downloading/installing, installed, failed (retried up to `maxAttempts`), stopped
 * after the attempts ran out, rolled back by the gateway itself. The list and its order come from GET /ota/config.
 */
export type OtaState = 'none' | 'queued' | 'waiting_window' | 'offline' | 'sent' | 'in_progress' | 'installed' | 'failed' | 'exhausted' | 'rolled_back';

/** How the gateways that target one version stand. */
export interface OtaStatusCounts {
  targeted: number;
  installed: number;
  inProgress: number;
  sent: number;
  queued: number;
  waitingWindow: number;
  offline: number;
  failed: number;
  exhausted: number;
  rolledBack: number;
}

/** A gateway software version: verified by the server and again on every gateway before it is installed. */
export interface Release {
  version: string;
  sha256: string;
  size: number;
  notes: string | null;
  createdAt: string;
  /** gateways running it now */
  running: number;
  /** gateways asked to run it */
  targeted: number;
}
export type ReleasePage = OffsetPage<Release>;
/** GET /ota/releases/:version: the counts per state, and whether it may be deleted (no gateway targets it). */
export interface ReleaseDetail extends Release {
  status: OtaStatusCounts;
  canDelete: boolean;
}

/** GET /ota/config: the maintenance window (null: none set, updates go out at once) and how often the scheduler runs. */
export interface OtaConfig {
  window: { start: string; end: string } | null;
  timezone: string;
  checkIntervalS: number;
  /** update commands per gateway and rollout before it is `exhausted` */
  maxAttempts: number;
  /** a command without a report is sent again after this long */
  retryAfterS: number;
  /** every OTA state, in display order */
  states: OtaState[];
}
/** GET /ota/summary: the latest version and how its rollout stands (both null before the first release). */
export interface OtaSummary {
  latest: Release | null;
  status: OtaStatusCounts | null;
}

/** A row of GET /ota/gateways. */
export interface OtaGateway {
  gwId: string;
  label: string | null;
  locationId: number | null;
  locationName: string | null;
  regencyName: string | null;
  provinceCode: string | null;
  provinceName: string | null;
  online: boolean;
  state: string;
  version: string | null;
  targetVersion: string | null;
  otaState: OtaState;
  attempts: number;
  /** a sentence safe to show (Indonesian), or null */
  lastError: string | null;
  updatedAt: string | null;
}
export type OtaGatewayPage = OffsetPage<OtaGateway>;

/** The filters of the gateway list, also a rollout's or a cancel's target. Every value is comma-separated (several). */
export interface OtaGatewayFilter {
  q?: string;
  /** the version it runs */
  version?: string;
  /** the version it is asked to run */
  target?: string;
  otaState?: string;
  provinceCode?: string;
  regencyCode?: string;
  /** "true", "false" or both */
  online?: string;
}

/** Which gateways a rollout or a cancel is for: these ids, every gateway, or every gateway a filter matches. */
export type OtaTarget = { gwIds: string[] } | { all: true } | { filter: OtaGatewayFilter };

export interface RolloutPreview {
  /** gateways the target matches */
  matching: number;
  /** those that will be asked to install (after the percentage, without the ones already installed or targeted) */
  selected: number;
  alreadyInstalled: number;
  offline: number;
  /** selected gateways now running a newer version */
  downgrade: number;
  /** up to 10 of the selected */
  sample: { gwId: string; label: string | null }[];
}
export interface RolloutBody {
  version: string;
  target: OtaTarget;
  /** 1..100: a deterministic share of the matching gateways */
  percent?: number;
}
export interface RolloutResult { version: string; selected: number; immediate: boolean }

export type OtaEventKind = 'targeted' | 'sent' | 'reported_in_progress' | 'installed' | 'failed' | 'rolled_back' | 'cancelled' | 'exhausted';
/** One step in a gateway's update history (append-only, newest first). */
export interface OtaEvent {
  id: number;
  version: string | null;
  kind: OtaEventKind;
  at: string;
  /** the entry as stored (attempt, the gateway's own text, who rolled it out): not for display */
  detail: Record<string, unknown>;
  /** why it failed, in plain Indonesian (failed, rolled_back, exhausted); null otherwise */
  error: string | null;
}
export type OtaEventPage = CursorPage<OtaEvent>;

export const otaPaths = {
  config: '/ota/config',
  summary: '/ota/summary',
  releases: (page: number, limit: number) => `/ota/releases${query({ page, limit })}`,
  release: (version: string) => `/ota/releases/${encodeURIComponent(version)}`,
  gateways: (f: OtaGatewayFilter & { page?: number; limit?: number }) => `/ota/gateways${query({ ...f })}`,
  events: (gwId: string, cursor: Cursor, limit: number) => `/gateways/${encodeURIComponent(gwId)}/ota-events${query({ ...cursor, limit })}`,
};

export const previewRollout = (body: RolloutBody) => send<RolloutPreview>('/ota/rollout/preview', body);
export const startRollout = (body: RolloutBody & { immediate: boolean; allowDowngrade?: boolean }) => send<RolloutResult>('/ota/rollout', body);
/** Failed and stopped gateways of a version try again (attempts start over); a gateway that rolled back is left alone. */
export const retryFailed = (version: string) => send<{ requeued: number }>('/ota/retry', { version });
export const cancelUpdates = (target: OtaTarget) => send<{ cancelled: number }>('/ota/cancel', { target });
export const deleteRelease = (version: string) => remove(otaPaths.release(version));
